import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import type { SpeciesApi } from "@/lib/api";
import { useClients, useIamRoles, useMunicipalities } from "@/hooks";
import { NoRealData } from "@/components/shared";
import SettingsKpiCard from "./components/SettingsKpiCard";
import SpeciesTable from "./components/SpeciesTable";
import FamilyTable from "./components/FamilyTable";
import CtaCatalogsPanel from "./components/CtaCatalogsPanel";
import UserManagementPanel from "./components/UserManagementPanel";
import ClientsTable from "./components/ClientsTable";
import MunicipalitiesTable from "./components/MunicipalitiesTable";
import MenuVisibilityPanel from "./components/MenuVisibilityPanel";
import IamAdminPanel from "./components/IamAdminPanel";

const tabs = [
  { id: "especies", label: "Espécies e Botânica" },
  { id: "risco", label: "Escalas do CTA" },
  { id: "manejo", label: "Manejo e Vistorias" },
  { id: "usuarios", label: "Usuários e Permissões" },
  { id: "clientes", label: "Clientes e Municípios" },
  { id: "menu", label: "Menu e Navegação" },
  { id: "iam", label: "Controle de Acesso (IAM)" },
  { id: "geral", label: "Geral" },
  { id: "gis", label: "GIS e Coordenadas" },
  { id: "integracoes", label: "Integrações" },
  { id: "aparencia", label: "Aparência e Sistema" },
  { id: "auditoria", label: "Auditoria" },
];

export default function Configuracoes() {
  const [activeTab, setActiveTab] = useState("especies");
  const [species, setSpecies] = useState<SpeciesApi[]>([]);
  const [speciesLoading, setSpeciesLoading] = useState(true);
  const [speciesError, setSpeciesError] = useState<string | null>(null);
  const { municipalities, loading: municipalitiesLoading } = useMunicipalities();
  const { clients, loading: clientsLoading } = useClients();
  const { roles, loading: rolesLoading } = useIamRoles();

  // Lista crua da API: o mapper de tela preenche porte/origem ausentes com valores
  // padrao, e aqui o catalogo tem de mostrar o que de fato esta gravado.
  useEffect(() => {
    let alive = true;
    api
      .species()
      .then((rows) => alive && setSpecies(rows))
      .catch((err) => alive && setSpeciesError(err instanceof Error ? err.message : "Erro ao carregar espécies"))
      .finally(() => alive && setSpeciesLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const familyCount = useMemo(
    () => new Set(species.map((s) => s.family_name).filter(Boolean)).size,
    [species]
  );

  const kpi = (loading: boolean, value: number) => (loading ? "—" : String(value));

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <div className="text-xs text-foreground-500 mb-1">Dashboard &gt; Configurações</div>
        <h1 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">Configurações</h1>
        <p className="text-sm text-foreground-500 mt-1 max-w-2xl">
          Cadastros auxiliares, usuários, permissões, catálogo de espécies e escalas técnicas do CTA.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
        <SettingsKpiCard label="Espécies cadastradas" value={kpi(speciesLoading, species.length)} icon="ri-plant-line" color="primary" subtitle="Catálogo botânico" />
        <SettingsKpiCard label="Famílias botânicas" value={kpi(speciesLoading, familyCount)} icon="ri-stack-line" color="primary" subtitle="Informadas no catálogo" />
        <SettingsKpiCard label="Perfis de acesso" value={kpi(rolesLoading, roles.length)} icon="ri-shield-keyhole-line" color="accent" subtitle="Papéis do IAM" />
        <SettingsKpiCard label="Municípios" value={kpi(municipalitiesLoading, municipalities.length)} icon="ri-map-pin-line" color="secondary" subtitle="Cadastrados" />
        <SettingsKpiCard label="Clientes" value={kpi(clientsLoading, clients.length)} icon="ri-building-line" color="secondary" subtitle="Contratantes" />
      </div>

      {/* Tabs */}
      <div className="border-b border-background-200">
        <div className="flex gap-1 overflow-x-auto pb-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap rounded-t-lg transition-colors border-b-2 ${
                activeTab === tab.id
                  ? "text-primary-700 border-primary-500 bg-primary-50/50"
                  : "text-foreground-500 border-transparent hover:text-foreground-700 hover:bg-background-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {activeTab === "especies" && (
          <div className="space-y-5">
            <h3 className="text-sm font-semibold text-foreground-900">Espécies cadastradas</h3>
            {speciesLoading ? (
              <p className="text-sm text-foreground-500">Carregando espécies...</p>
            ) : speciesError ? (
              <p className="text-sm text-danger-600">Não foi possível carregar: {speciesError}</p>
            ) : (
              <>
                <SpeciesTable species={species} />
                <div className="border-t border-background-200 pt-5 mt-2">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-3">Famílias botânicas</h3>
                  <FamilyTable species={species} />
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === "risco" && <CtaCatalogsPanel />}

        {activeTab === "manejo" && (
          <NoRealData
            icon="ri-scissors-line"
            title="Tipos de manejo e de vistoria"
            description="Ainda não há catálogo de tipos de manejo e de vistoria servido pelo backend. As opções em uso aparecem no formulário de vistoria."
          />
        )}

        {activeTab === "usuarios" && <UserManagementPanel />}

        {activeTab === "clientes" && (
          <div className="space-y-5">
            <div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-1">Clientes e contratantes</h3>
              <p className="text-xs text-foreground-500 mb-4">Organizações contratantes cadastradas</p>
              {clientsLoading ? <p className="text-sm text-foreground-500">Carregando...</p> : <ClientsTable clients={clients} />}
            </div>
            <div className="border-t border-background-200 pt-5">
              <h3 className="text-sm font-semibold text-foreground-900 mb-1">Municípios</h3>
              <p className="text-xs text-foreground-500 mb-4">Municípios cadastrados na plataforma</p>
              {municipalitiesLoading ? (
                <p className="text-sm text-foreground-500">Carregando...</p>
              ) : (
                <MunicipalitiesTable municipalities={municipalities} />
              )}
            </div>
          </div>
        )}

        {activeTab === "menu" && <MenuVisibilityPanel />}

        {activeTab === "iam" && <IamAdminPanel />}

        {activeTab === "geral" && (
          <NoRealData
            icon="ri-settings-3-line"
            title="Dados da organização e padrões gerais"
            description="Ainda não há configuração geral persistida no servidor (nome da organização, contato, fuso, formato de data, padrões do código CTA). O código CTA é gerado pelo servidor."
          />
        )}

        {activeTab === "gis" && (
          <NoRealData
            icon="ri-map-pin-line"
            title="Parâmetros GIS e validação de geometrias"
            description="Ainda não há configuração GIS persistida. A API trabalha em EPSG:4326 e a validação geométrica fica nas views do banco."
          />
        )}

        {activeTab === "integracoes" && (
          <NoRealData
            icon="ri-plug-line"
            title="Integrações"
            description="Ainda não há registro real de integrações e do estado de cada uma."
          />
        )}

        {activeTab === "aparencia" && (
          <NoRealData
            icon="ri-palette-line"
            title="Aparência e sistema"
            description="Preferências de aparência ainda não são salvas. A visibilidade do menu é configurada na aba Menu e Navegação."
          />
        )}

        {activeTab === "auditoria" && (
          <NoRealData
            icon="ri-file-search-line"
            title="Auditoria do sistema"
            description="Ainda não há trilha de auditoria geral. Os eventos de acesso estão em Controle de Acesso (IAM) > Auditoria IAM; o histórico de cada exemplar está na ficha dele."
          />
        )}
      </div>
    </div>
  );
}
